/**
 * ThumbHealButton — Studio-header admin tool. Scans the whole library
 * for stories whose thumbnail vanished (file missing from disk AND
 * Object Store, or no thumbnail_url at all) and regenerates the
 * standard auto video-frame for each, one at a time in the background.
 */
import { useState } from "react";
import axios from "axios";
import { ImageOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ThumbHealButton({ adminToken }) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/thumbnails/heal`,
        {},
        { headers: { "X-Admin-Token": adminToken || "storylo-admin" } }
      );
      if (data.already_running) {
        toast.info(
          `Thumbnail heal already running — ${data.done}/${data.total} done` +
            (data.current ? ` (now: ${data.current})` : "")
        );
      } else {
        toast.success(
          "Thumbnail heal started — scanning the library for missing thumbnails and regenerating them from each video. Click again anytime to see progress."
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Thumbnail heal failed to start");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={() => {
        if (busy) return;
        if (!confirm) {
          setConfirm(true);
          setTimeout(() => setConfirm(false), 5000);
          return;
        }
        setConfirm(false);
        run();
      }}
      disabled={busy}
      className={`text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-md border inline-flex items-center gap-1.5 disabled:opacity-40 ${confirm ? "border-amber-400 text-amber-300" : "border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"}`}
      data-testid="heal-thumbnails-btn"
      title="Find every story whose thumbnail went missing and regenerate it from the video (frame at 1s). Runs in the background."
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageOff className="w-3 h-3" />}
      {confirm ? "Regenerate missing?" : "Heal thumbnails"}
    </button>
  );
}
